/**
 * Quick filters for the events grid (Today / Tomorrow / This Weekend / nearby).
 * Everything works off the normalized event model from `lib/catalog`.
 */
import { addDays, endOfWeek, format, isValid, parseISO, startOfDay } from 'date-fns';

const iso = (date) => format(date, 'yyyy-MM-dd');

/** The days an event runs, as a Set for O(1) membership tests. */
const dayset = (event) => new Set(event.days || []);

/** Saturday + Sunday of the current week. */
export const weekendDays = (today = new Date()) => {
  const end = endOfWeek(today, { weekStartsOn: 1 }); // Sunday
  return [iso(addDays(end, -1)), iso(end)];
};

export const QUICK_FILTERS = [
  {
    key: 'today',
    label: 'Today',
    match: (e) => dayset(e).has(iso(startOfDay(new Date()))),
  },
  {
    key: 'tomorrow',
    label: 'Tomorrow',
    match: (e) => dayset(e).has(iso(addDays(startOfDay(new Date()), 1))),
  },
  {
    key: 'weekend',
    label: 'This Weekend',
    match: (e) => {
      const days = dayset(e);
      return weekendDays().some((d) => days.has(d));
    },
  },
  {
    key: 'nearby',
    label: 'Under 10 km',
    match: (e) => e.distanceKm != null && e.distanceKm <= 10,
  },
  {
    // A teaser has no tiers yet, so priceFrom falls back to 0 and isFree reads
    // true — which would put every unpriced announcement under "Free entry".
    key: 'free',
    label: 'Free entry',
    match: (e) => e.isFree && !e.comingSoon,
  },
  {
    key: 'offers',
    label: 'On offer',
    match: (e) => e.discountPct > 0 && !e.comingSoon,
  },
];

export const SORTS = {
  recommended: { label: 'Recommended', compare: null },
  date: {
    label: 'Date',
    compare: (a, b) => String(a.startDate || '9').localeCompare(String(b.startDate || '9')),
  },
  distance: {
    label: 'Distance',
    compare: (a, b) => (a.distanceKm ?? 1e9) - (b.distanceKm ?? 1e9),
  },
  // Unpriced teasers sort last either way rather than anchoring the cheap end
  // with a placeholder 0.
  price_low: {
    label: 'Price: Low to High',
    compare: (a, b) =>
      Number(a.comingSoon) - Number(b.comingSoon) || a.priceFrom - b.priceFrom,
  },
  price_high: {
    label: 'Price: High to Low',
    compare: (a, b) =>
      Number(a.comingSoon) - Number(b.comingSoon) || b.priceFrom - a.priceFrom,
  },
};

/** Distinct `event_type` values present in the live data — admin-driven chips. */
export const collectEventTypes = (events = []) => {
  const set = new Set();
  events.forEach((e) => (e.eventTypes || []).forEach((t) => set.add(t)));
  return Array.from(set).sort();
};

/** Events whose run has already finished are hidden from the storefront. */
export const isUpcoming = (event) => {
  if (!event?.endDate) return true; // no date info — don't hide it
  const end = parseISO(event.endDate);
  if (!isValid(end)) return true;
  return end >= startOfDay(new Date());
};

export const applyFilters = (events, { query = '', quick = [], types = [], sort = 'recommended' } = {}) => {
  const q = query.trim().toLowerCase();
  const quickMatchers = QUICK_FILTERS.filter((f) => quick.includes(f.key));

  const filtered = events.filter((e) => {
    if (q) {
      const haystack = [
        e.title,
        e.venueName,
        e.location?.address,
        e.categoryName,
        ...(e.eventTypes || []),
        // Searching "comedy" should find events filed under Comedy even when
        // the word appears nowhere in the title.
        ...(e.subcategories || []).map((s) => s.name),
        // …and an artist's name finds their shows.
        ...(e.artists || []).map((a) => a.name),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(q)) return false;
    }
    if (types.length && !(e.eventTypes || []).some((t) => types.includes(t))) return false;
    // Quick filters are ANDed — "Today" + "Under 10 km" means both.
    return quickMatchers.every((f) => f.match(e));
  });

  const compare = SORTS[sort]?.compare;
  return compare ? [...filtered].sort(compare) : filtered;
};
